import React, { useState, useEffect, useCallback } from 'react';

/**
 * Checks whether a value is an integer (or a string that represents one without decimals).
 */
const isInteger = (x: string): boolean => {
  return !!x && x.indexOf('.') < 0 && Number(x) % 1 === 0;
};

/** Synthetic event shape emitted by TimePicker's onChange callback. */
interface TimePickerChangeEvent {
  target: {
    name: string;
    value: string;
    type: 'text';
  };
}

interface TimePickerProps {
  id: string;
  name: string;
  value: string;
  onChange: (event: TimePickerChangeEvent) => void;
  inputClassNames?: string;
}

/**
 * A simple HH:mm time input using two controlled text fields (hour and minute).
 * Emits a synthetic change event with the combined "HH:mm" value.
 */
const TimePicker = ({ id, name, value, onChange, inputClassNames }: TimePickerProps) => {
  const parseTime = useCallback(
    (val: string): { hour: string; minute: string } => ({
      hour: val ? val.split(':')[0] : '',
      minute: val ? val.split(':')[1] : '',
    }),
    []
  );

  const [hour, setHour] = useState<string>(() => parseTime(value).hour);
  const [minute, setMinute] = useState<string>(() => parseTime(value).minute);

  useEffect(() => {
    const parsed = parseTime(value);
    setHour(parsed.hour);
    setMinute(parsed.minute);
  }, [value, parseTime]);

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>): void => {
    const fieldName = event.target.name;
    let fieldValue = event.target.value;

    if (!isInteger(fieldValue) || Number(fieldValue) > 9999999999) {
      return;
    }

    if (fieldName === 'hour' && Number(fieldValue) > 23) {
      return;
    }
    if (fieldName === 'minute' && Number(fieldValue) > 59) {
      return;
    }

    if (fieldValue.length < 1) {
      fieldValue = '00';
    } else if (fieldValue.length < 2) {
      fieldValue = '0' + fieldValue;
    } else if (fieldValue.length > 2) {
      fieldValue = fieldValue.substring(fieldValue.length - 2, fieldValue.length);
    }

    const newHour = fieldName === 'hour' ? fieldValue : hour;
    const newMinute = fieldName === 'minute' ? fieldValue : minute;

    setHour(newHour);
    setMinute(newMinute);

    onChange({
      target: {
        name,
        value: newHour + ':' + newMinute,
        type: 'text',
      },
    });
  };

  return (
    <div style={{ display: 'inline' }}>
      <input
        type="text"
        className={inputClassNames}
        style={{ display: 'inline', width: '20px', margin: '0 5px 0 10px' }}
        id={id + '-hour'}
        name="hour"
        value={hour}
        onChange={handleChange}
        placeholder="HH"
      />
      :
      <input
        type="text"
        className={inputClassNames}
        style={{ display: 'inline', width: '20px', margin: '0 5px 0 10px' }}
        id={id + '-minute'}
        name="minute"
        value={minute}
        onChange={handleChange}
        placeholder="mm"
      />
    </div>
  );
};

export default TimePicker;
